import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, X, ClipboardList, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n";
import { api, getClientId } from "../../lib/api";
import { factorTags } from "../../data/safety";

const OPTIONS = {
  category: [
    { id: "horse", de: "Pferd", en: "Horse" },
    { id: "equipment", de: "Ausrüstung", en: "Equipment" },
    { id: "environment", de: "Umgebung", en: "Environment" },
    { id: "rider", de: "Reiter", en: "Rider" },
    { id: "other", de: "Sonstiges", en: "Other" },
  ],
  phase: [
    { id: "groundwork", de: "Bodenarbeit", en: "Groundwork" },
    { id: "pre_ride", de: "Vorbereitung", en: "Pre-ride" },
    { id: "mounting", de: "Aufsteigen", en: "Mounting" },
    { id: "riding", de: "Reiten", en: "Riding" },
    { id: "dismount", de: "Absteigen", en: "Dismount" },
    { id: "other", de: "Sonstiges", en: "Other" },
  ],
  severity: [
    { id: "near_miss", de: "Beinah-Unfall", en: "Near-miss" },
    { id: "minor", de: "Leicht", en: "Minor" },
    { id: "serious", de: "Ernst", en: "Serious" },
  ],
  location_type: [
    { id: "arena", de: "Halle", en: "Arena" },
    { id: "field", de: "Platz", en: "Field" },
    { id: "trail", de: "Gelände", en: "Trail" },
    { id: "stable", de: "Stall", en: "Stable" },
    { id: "transport", de: "Transport", en: "Transport" },
    { id: "other", de: "Sonstiges", en: "Other" },
  ],
};

const sevColor = { near_miss: "text-sonne border-sonne/40", minor: "text-bronze-soft border-bronze/40", serious: "text-danger border-danger/40" };

export default function NearMissLog() {
  const { t, lang } = useLang();
  const clientId = getClientId();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const label = (o) => (lang === "de" ? o.de : o.en);

  const empty = {
    occurred_on: new Date().toISOString().slice(0, 10),
    category: "horse",
    phase: "riding",
    severity: "near_miss",
    location_type: "arena",
    contributing_factors: [],
    horse_name: "",
    notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([
        api.get(`/near-miss`, { params: { client_id: clientId } }),
        api.get(`/near-miss/stats/summary`, { params: { client_id: clientId } }),
      ]);
      setItems(list.data);
      setStats(s.data);
    } catch (err) {
      console.error("Failed to load near-miss data", err);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const toggleFactor = (id) =>
    setForm((f) => ({ ...f, contributing_factors: f.contributing_factors.includes(id) ? f.contributing_factors.filter((x) => x !== id) : [...f.contributing_factors, id] }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/near-miss", { client_id: clientId, ...form });
      setForm(empty);
      setOpen(false);
      toast.success(lang === "de" ? "Eintrag gespeichert." : "Entry saved.");
      load();
    } catch (err) {
      console.error("Failed to save near-miss entry", err);
      toast.error("Fehler / Error");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/near-miss/${id}`, { params: { client_id: clientId } });
      load();
    } catch (err) {
      console.error("Failed to delete near-miss entry", err);
      toast.error("Fehler / Error");
    }
  };

  const optLabel = (group, id) => {
    const o = OPTIONS[group].find((x) => x.id === id);
    return o ? label(o) : id;
  };

  return (
    <div>
      <p className="text-sm text-wachs-dim leading-relaxed max-w-2xl">{t.safety.log.intro}</p>
      <p className="mt-3 font-mono text-xs text-wachs-dim/70 border border-bronze/15 rounded-md px-3 py-2 inline-block">🔒 {t.safety.log.privacy}</p>

      {/* Stats */}
      {stats && stats.total > 0 && (
        <div className="mt-6 grid-panel rounded-lg p-5">
          <div className="flex items-center gap-2 text-sonne"><BarChart3 size={16} /><span className="instrument-label">{t.safety.log.stats}</span></div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBlock label={lang === "de" ? "Gesamt" : "Total"} value={stats.total} data={null} optLabel={null} />
            <StatBlock label={t.safety.log.form.severity} data={stats.by_severity} optLabel={(id) => optLabel("severity", id)} />
            <StatBlock label={t.safety.log.form.category} data={stats.by_category} optLabel={(id) => optLabel("category", id)} />
            <StatBlock label={t.safety.log.form.phase} data={stats.by_phase} optLabel={(id) => optLabel("phase", id)} />
          </div>
        </div>
      )}

      {/* Add button */}
      <button onClick={() => setOpen((o) => !o)} data-testid="nearmiss-add-toggle" className="mt-6 inline-flex items-center gap-2 bg-bronze text-nacht font-medium px-5 py-2.5 rounded-md hover:bg-bronze-soft transition-colors">
        {open ? <X size={16} /> : <Plus size={16} />} {open ? t.safety.log.form.cancel : t.safety.log.add}
      </button>

      {/* Form */}
      <AnimatePresence>
        {open && (
          <motion.form
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            onSubmit={submit} data-testid="nearmiss-form" className="mt-4 grid-panel rounded-lg p-5 overflow-hidden"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={t.safety.log.form.date}>
                <input type="date" required value={form.occurred_on} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} data-testid="nearmiss-date" className="coi-input" />
              </Field>
              <Field label={t.safety.log.form.horse}>
                <input type="text" value={form.horse_name} onChange={(e) => setForm({ ...form, horse_name: e.target.value })} data-testid="nearmiss-horse" className="coi-input" placeholder="—" />
              </Field>
              {["category", "phase", "severity", "location_type"].map((g) => (
                <Field key={g} label={t.safety.log.form[g === "location_type" ? "location" : g]}>
                  <select value={form[g]} onChange={(e) => setForm({ ...form, [g]: e.target.value })} data-testid={`nearmiss-${g}`} className="coi-input">
                    {OPTIONS[g].map((o) => <option key={o.id} value={o.id}>{label(o)}</option>)}
                  </select>
                </Field>
              ))}
            </div>

            <div className="mt-4">
              <p className="instrument-label text-wachs-dim mb-2">{t.safety.log.form.factors}</p>
              <div className="flex flex-wrap gap-2">
                {factorTags.map((f) => {
                  const on = form.contributing_factors.includes(f.id);
                  return (
                    <button type="button" key={f.id} onClick={() => toggleFactor(f.id)} data-testid={`nearmiss-factor-${f.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-sonne/20 border-sonne text-sonne" : "border-wachs-dim/30 text-wachs-dim hover:border-sonne/50"}`}>
                      {label(f)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <Field label={t.safety.log.form.notes}>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="nearmiss-notes" className="coi-input resize-none" />
              </Field>
            </div>

            <button type="submit" data-testid="nearmiss-save" className="mt-4 bg-sonne text-nacht font-medium px-5 py-2.5 rounded-md hover:opacity-90 transition-opacity">
              {t.safety.log.form.save}
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* List */}
      <div className="mt-8">
        {items.length === 0 ? (
          <div className="flex items-center gap-3 text-wachs-dim text-sm border border-dashed border-bronze/25 rounded-lg p-6">
            <ClipboardList size={18} /> {t.safety.log.empty}
          </div>
        ) : (
          <ul className="space-y-3" data-testid="nearmiss-list">
            {items.map((it) => (
              <li key={it.id} className="grid-panel rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-wachs-dim">{it.occurred_on}</span>
                      <span className={`font-mono text-xs px-2 py-0.5 rounded border ${sevColor[it.severity] || ""}`}>{optLabel("severity", it.severity)}</span>
                      <span className="text-xs text-wachs-dim">· {optLabel("category", it.category)} · {optLabel("phase", it.phase)} · {optLabel("location_type", it.location_type)}</span>
                      {it.horse_name && <span className="text-xs text-bronze-soft">· {it.horse_name}</span>}
                    </div>
                    {it.contributing_factors?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {it.contributing_factors.map((fid) => {
                          const f = factorTags.find((x) => x.id === fid);
                          return <span key={fid} className="text-[10px] px-2 py-0.5 rounded-full bg-tiefsee-2/60 text-wachs-dim">{f ? label(f) : fid}</span>;
                        })}
                      </div>
                    )}
                    {it.notes && <p className="mt-2 text-sm text-wachs-dim leading-snug">{it.notes}</p>}
                  </div>
                  <button onClick={() => remove(it.id)} data-testid={`nearmiss-delete-${it.id}`} className="text-wachs-dim hover:text-danger transition-colors shrink-0">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="instrument-label text-wachs-dim">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const StatBlock = ({ label, value, data, optLabel }) => (
  <div>
    <p className="instrument-label text-wachs-dim/70">{label}</p>
    {value !== undefined && data === null ? (
      <p className="font-mono text-3xl text-sonne mt-1">{value}</p>
    ) : (
      <ul className="mt-1 space-y-0.5">
        {Object.entries(data || {}).map(([k, v]) => (
          <li key={k} className="font-mono text-xs text-wachs flex justify-between gap-2"><span className="text-wachs-dim">{optLabel(k)}</span><span>{v}</span></li>
        ))}
      </ul>
    )}
  </div>
);
